import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface IpcTdAccountingParams {
  mailCategories: string[];
  accountingType: string;
  year: number;
  formatType: 'html' | 'pdf' | 'xls';
  direction?: string;
  designatedOperator?: string;
}

// Data structure for IpcTdAccounting results
export interface IpcTdAccountingDataStructure {
  operator: string;
  items: IpcTdAccountingItem[];
}

export interface IpcTdAccountingItem {
  serviceType: string;
  accountingType: string;
  fileName: string;
  rows: IpcTdAccountingRow[];
}

export interface IpcTdAccountingRow {
  outboundOe: string;
  inboundOe: string;
  rcLabel: string;
  gross: number;
  tare: number;
  net: number;
  receivedReceptacleStratum: string;
  numberOfSamples: number;
  effectiveSamplingRateStratum: number;
  netIpk: number;
  pkg: number;
  gkg: number;
  ekg: number;
  pitems: number;
  gitems: number;
  eitems: number;
  jitems: number;
  zitems: number;
  jkg: number;
  zkg: number;
}

export interface IpcTdAccountingResponse {
  // For HTML format, contains the table data
  data?: IpcTdAccountingDataStructure[];
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
}

export function useIpcTdAccounting() {
  return useMutation<IpcTdAccountingResponse, Error, IpcTdAccountingParams>({
    mutationFn: async (params: IpcTdAccountingParams) => {
      const searchParams = new URLSearchParams();
      
      params.mailCategories.forEach(category => {
        searchParams.append('mailCategories', category);
      });
      
      searchParams.append('accountingType', params.accountingType);
      searchParams.append('year', params.year.toString());
      searchParams.append('formatType', params.formatType);
      if (params.direction) {
        searchParams.append('direction', params.direction);
      }
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }

      const response = await axios.get(`/reports/no3-cn56-summary?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Try to match quoted filename first: filename="example.pdf"
        let filenameMatch = /filename\*?=['"]?([^"';]+)['"]?/i.exec(contentDisposition);
        
        // If no match, try unquoted filename
        if (!filenameMatch) {
          filenameMatch = /filename[^;=\n]*=([^;,\n]+)/i.exec(contentDisposition);
        }
        
        if (filenameMatch && filenameMatch[1]) {
          // Remove any trailing spaces and decode if needed
          fileName = filenameMatch[1].trim();
          // Decode URI component only if it looks like it's encoded
          if (fileName.includes('%')) {
            fileName = decodeURIComponent(fileName);
          }
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `NO3_CN56_Summary_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
} 